<template>
  <div>
    <b-table :fields="fields"
             :items="documents"
             :sort-by="defaultSortKey"
             :sort-desc="true"
             head-variant="light"
             hover
             responsive
             striped>

      <template v-slot:top-row="{fields}">
        <proposal-filter :id="id"
                         v-model="documents"
                         :fields="fields"
                         :not-include-key="['actions', 'ordinalNumber']"/>
      </template>

      <template v-slot:cell(ordinalNumber)="row">
        <base-column-ordinal-number v-bind="{index: row.index, perPage:0, currentPage:1}"/>
      </template>

      <template v-slot:cell(actions)="row">

        <template v-if="isEditable(row.item)">
          <base-icon-button-edit title="Edit proposal"
                                 @click="goToProposalForm(row.item)"/>

          <base-icon-button-delete title="Delete proposal"
                                   @click="openConfirmationModal(getId(row.item))"/>
        </template>

        <action-download-pdf :disabled="false"
                             @click="handlePdf(row.item)"/>

        <base-material-design-icon-button v-if="isSubmitted"
                                          :disabled="!isEvaluationVisibleToUser(row.item)"
                                          icon="fact_check"
                                          title="Show evaluation"
                                          @click="openEvaluationModal(row.item)"/>

        <action-download-additional-sample-declaration-pdf v-if="isAdditionalSampleDeclaration(row.item)"
                                                           :proposal="row.item"/>

        <action-download-report-pdf v-if="isSubmitted && isExperimentReport(row.item)"
                                    :proposal="row.item"/>

        <base-material-design-icon-button v-if="isSubmitted && isEvaluationVisibleToUser(row.item)"
                                          icon="assignment_late"
                                          title="Add experiment report"
                                          @click="goToReport(row.item)"/>

        <base-material-design-icon-button v-if="isSubmitted"
                                          icon="rate_review"
                                          title="Add feedback"
                                          @click="goToFeedback(row.item)"/>
      </template>
    </b-table>
    <base-submit-danger-modal ref="submitConfirmationModal"
                              :confirm-function="deleteDocument"
                              confirm-button-title="Confirm"
                              modal-title="Confirm deletion">
      <p class="my-1 p-1 text-center">Do you want to delete this proposal?</p>
      <p class="p-1 text-center custom-text-danger">Deleting is final and cannot be revoked.</p>
    </base-submit-danger-modal>
  </div>
</template>

<script>
  import ActionDownloadPdf from '@/components/actions/ActionDownloadPdf';
  import ActionDownloadAdditionalSampleDeclarationPdf from
  '@/components/actions/ActionDownloadAdditionalSampleDeclarationPdf';
  import ActionDownloadReportPdf from '@/components/actions/ActionDownloadReportPdf';
  import BaseColumnOrdinalNumber from '@/components/base-components/BaseColumnOrdinalNumber';
  import BaseIconButtonDelete from '@/components/base-components/base-icons/BaseIconButtonDelete';
  import BaseIconButtonEdit from '@/components/base-components/base-icons/BaseIconButtonEdit';
  import BaseSubmitDangerModal from '@/components/base-components/base-modals/BaseSubmitDangerModal';
  import BaseMaterialDesignIconButton from '@/components/base-components/base-icons/BaseMaterialDesignIconButton';
  import ProposalFilter from '@/components/ProposalFilter';
  import { calls as urlLayer } from '@/api/url_layer';
  import { getId } from '@/helpers';
  import { isProposalOfType } from '@/helpers/proposal-form';
  import { mapGetters } from 'vuex';

  export default {
    components: {
      ActionDownloadPdf,
      ActionDownloadAdditionalSampleDeclarationPdf,
      ActionDownloadReportPdf,
      BaseColumnOrdinalNumber,
      BaseIconButtonDelete,
      BaseIconButtonEdit,
      BaseMaterialDesignIconButton,
      BaseSubmitDangerModal,
      ProposalFilter,
    },
    props: {
      fields: {
        type: Array,
        required: true,
      },
      mainUrlPart: {
        type: String,
        required: true,
      },
      proposalFormUrlPart: {
        type: String,
        required: true,
      },
      infrastructure: {
        type: String,
        required: true,
      },
      refreshSubmitted: {
        type: Boolean,
        default: false,
      },
      isSubmitted: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        documents: [],
        id: 1,
        inEvaluationStatus: 'in evaluation',
        calls: [],
        proposalIdToDelete: '',
      };
    },
    computed: {
      ...mapGetters('calls', { openCallId: 'current_call_id' }),
      proposalStateFromUrl() {
        return this.mainUrlPart.split('/').find((el) => ['submitted', 'draft'].includes(el));
      },
      defaultSortKey() {
        return this.proposalStateFromUrl === 'submitted' ? 'states.date' : 'created_at';
      },
    },
    methods: {
      goToProposalForm(item) {
        const proposalId = this.getId(item);
        this.$router.push({ path: `/proposals/${this.proposalFormUrlPart}${proposalId}` });
      },
      isAdditionalSampleDeclaration(item) {
        return item.additional_sample_declaration?.states.name === 'submitted';
      },
      isCallOpen(callId) {
        return callId === this.openCallId;
      },
      isExperimentReport(item) {
        return item.experiment_report?.states.name === 'submitted';
      },
      isProposalCeric(item) {
        const proposalSubtypesObject = item.document_specification.subtype || {};
        return Object.keys(proposalSubtypesObject).includes('ceric');
      },
      isProposalRapidAccess(item) {
        return isProposalOfType(item.general, 'Rapid access');
      },
      isProposalOfStandardType(item) {
        return !this.isProposalCeric(item) && !this.isProposalRapidAccess(item);
      },
      isEditable(item) {
        if (this.proposalStateFromUrl === 'submitted') {
          return this.isCallOpen(item.call_id) && this.isProposalOfStandardType(item);
        }
        return true;
      },
      handlePdf(item) {
        const objectId = this.getId(item);
        if (item.pdf_id) {
          this.downloadDocumentPdf(objectId);
        } else {
          this.generatePdf(objectId);
        }
      },
      openConfirmationModal(proposalId) {
        this.proposalIdToDelete = proposalId;
        this.$refs.submitConfirmationModal.show();
      },
      closeConfirmationModal() {
        this.$refs.submitConfirmationModal.hide();
      },
      async deleteDocument() {
        try {
          await this.axios.delete(`/documents/${this.proposalIdToDelete}`);
          this.removeFromDocuments(this.proposalIdToDelete);
          this.$notify({ type: 'success', title: 'Proposal deleted successfully!' });
        } catch {
          this.notifyDeletingError();
        } finally {
          this.closeConfirmationModal();
        }
      },
      findDocumentIndex(id) {
        return this.documents.findIndex((doc) => this.getId(doc) === id);
      },
      removeFromDocuments(id) {
        const idx = this.findDocumentIndex(id);
        if (idx > -1) {
          this.$delete(this.documents, idx);
        }
      },
      notifyDeletingError() {
        this.$notify({ type: 'error', title: 'The proposal couldn\'t be deleted' });
      },
      processId() {
        this.documents = this.documents.map((document) => {
          const { document_specification = {} } = document;
          // eslint-disable-next-line camelcase
          if ('document_id' in document_specification) {
            return document;
          }
          // eslint-disable-next-line camelcase
          document_specification.document_id = '- (ID will be generated after the call is closed)';
          return {
            ...document,
            // eslint-disable-next-line camelcase
            document_specification,
          };
        });
      },
      getAcceptanceMarkVisibleToUser(document, evaluationsData) {
        if (this.isProposalOfStandardType(document)) {
          const proposalCall = this.calls.find(
            (call) => getId(call) === document.call_id,
          );
          if (proposalCall === undefined || !proposalCall.is_evaluation_visible_to_user) {
            return this.inEvaluationStatus;
          }
        }
        return evaluationsData.acceptance_mark || this.inEvaluationStatus;
      },
      processEvaluationStatus() {
        this.documents = this.documents.map((document) => {
          // eslint-disable-next-line camelcase
          const { evaluations_data = {} } = document;
          // eslint-disable-next-line camelcase
          evaluations_data.acceptance_mark = this.getAcceptanceMarkVisibleToUser(document, evaluations_data);
          // eslint-disable-next-line camelcase
          return { ...document, evaluations_data };
        });
      },
      filterOutCericDocuments() {
        this.documents = this.documents.filter((document) => {
          const proposalSubtypesObject = document.document_specification.subtype || {};
          return !('ceric' in proposalSubtypesObject);
        });
      },
      processDocuments(documents) {
        this.documents = [];
        documents.forEach((document) => {
          // eslint-disable-next-line camelcase
          const created_at = this.createdAt(document);
          // eslint-disable-next-line camelcase
          this.documents.push({ ...document, created_at });
        });
        if (this.proposalStateFromUrl === 'submitted') {
          this.processId();
          this.processEvaluationStatus();
        } else {
          this.filterOutCericDocuments();
        }
      },
      async loadData() {
        const { data } = await this.axios.get(this.mainUrlPart);
        this.processDocuments(data.documents);
        // setup the data in proposal-filter, call-filter component
        this.$emit('setup-proposal', this.documents);
      },
      async loadCalls() {
        urlLayer.load_calls().then(
          (response) => {
            if (response.status === 200) {
              this.calls = response.data;
            }
          },
        ).catch(() => {
          this.$notify({
            type: 'error',
            title: 'Calls data cannot be loaded, please try again in a moment or contact User Office',
          });
        });
      },
      openEvaluationModal(currentProposal) {
        this.$emit('open-evaluation-modal', currentProposal);
      },
      isEvaluationVisibleToUser(currentProposal) {
        return currentProposal.evaluations_data?.acceptance_mark !== this.inEvaluationStatus;
      },
      goToFeedback(proposal) {
        const proposalId = this.getId(proposal);
        if (proposal.feedbacks) {
          this.$router.push(`/feedbacks/${proposalId}/${proposal.feedbacks[0].user_id}`);
        } else {
          this.$router.push(`/feedbacks/${proposalId}/${proposalId}`);
        }
      },
      goToReport(proposal) {
        const proposalId = this.getId(proposal);
        this.$router.push(`/documents/${proposalId}/experiment_reports`);
      },
    },
    watch: {
      refreshSubmitted() {
        if (this.refreshSubmitted) {
          this.loadData();
          this.$emit('set-refresh', false);
        }
      },
    },
    async created() {
      if (this.isSubmitted) {
        await this.loadCalls();
      }
      await this.loadData();
    },
  };
</script>

<style lang="scss"
       scoped>

</style>
